import type { PathParameters } from '@atlassian/eoc-schedule-common';

const urls: Readonly<{
	readonly alerts: {
		readonly responders: {
			readonly autoComplete: {
				readonly url: () => '/v1/alerts/responders/auto-complete';
			};
			readonly names: {
				readonly url: () => '/v1/alerts/responders/names';
			};
		};
	};
	readonly customers: {
		readonly timezones: {
			readonly url: () => '/v1/customers/timezones';
		};
	};
	readonly schedules: {
		readonly scheduleId: {
			readonly clone: {
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly disable: {
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly enable: {
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly name: {
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly overrides: {
				readonly alias: {
					readonly url: (pathParameters?: PathParameters<'scheduleId' | 'alias'>) => string;
				};
				readonly bulk: {
					readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
				};
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly rotations: {
				readonly rotationId: {
					readonly name: {
						readonly url: (pathParameters?: PathParameters<'scheduleId' | 'rotationId'>) => string;
					};
					readonly order: {
						readonly url: (pathParameters?: PathParameters<'scheduleId' | 'rotationId'>) => string;
					};
					readonly url: (pathParameters?: PathParameters<'scheduleId' | 'rotationId'>) => string;
				};
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly timeline: {
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly timezone: {
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
			readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			readonly webCalendarUrl: {
				readonly url: (pathParameters?: PathParameters<'scheduleId'>) => string;
			};
		};
		readonly teams: {
			readonly teamId: {
				readonly rights: {
					readonly url: (pathParameters?: PathParameters<'teamId'>) => string;
				};
				readonly url: (pathParameters?: PathParameters<'teamId'>) => string;
			};
		};
		readonly timeTemplates: {
			readonly rotation: {
				readonly url: () => '/v1/schedules/time-templates/rotation';
			};
		};
		readonly url: () => '/v1/schedules';
	};
	readonly uiPreference: {
		readonly schedule: {
			readonly team: {
				readonly teamId: {
					readonly url: (pathParameters?: PathParameters<'teamId'>) => string;
				};
			};
		};
	};
}> = Object.freeze({
	alerts: {
		responders: {
			names: {
				url: (): '/v1/alerts/responders/names' => '/v1/alerts/responders/names',
			},
			autoComplete: {
				url: (): '/v1/alerts/responders/auto-complete' => '/v1/alerts/responders/auto-complete',
			},
		},
	},
	schedules: {
		url: (): '/v1/schedules' => `/v1/schedules`,
		teams: {
			teamId: {
				url: ({ teamId }: PathParameters<'teamId'> = { teamId: ':teamId' }): string =>
					`/v1/schedules/teams/${teamId}`,
				rights: {
					url: ({ teamId }: PathParameters<'teamId'> = { teamId: ':teamId' }): string =>
						`/v1/schedules/teams/${teamId}/rights`,
				},
			},
		},
		scheduleId: {
			url: (
				{ scheduleId }: PathParameters<'scheduleId'> = {
					scheduleId: ':scheduleId',
				},
			): string => `/v1/schedules/${scheduleId}`,
			clone: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/clone`,
			},
			name: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/name`,
			},
			timezone: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/timezone`,
			},
			enable: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/enable`,
			},
			disable: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/disable`,
			},
			webCalendarUrl: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/web-calendar-url`,
			},
			timeline: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/timeline`,
			},
			overrides: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/overrides`,
				alias: {
					url: (
						{ scheduleId, alias }: PathParameters<'scheduleId' | 'alias'> = {
							scheduleId: ':scheduleId',
							alias: ':alias',
						},
					): string => `/v1/schedules/${scheduleId}/overrides/${alias}`,
				},
				bulk: {
					url: (
						{ scheduleId }: PathParameters<'scheduleId'> = {
							scheduleId: ':scheduleId',
						},
					): string => `/v1/schedules/${scheduleId}/overrides/bulk`,
				},
			},
			rotations: {
				url: (
					{ scheduleId }: PathParameters<'scheduleId'> = {
						scheduleId: ':scheduleId',
					},
				): string => `/v1/schedules/${scheduleId}/rotations`,
				rotationId: {
					url: (
						{ scheduleId, rotationId }: PathParameters<'scheduleId' | 'rotationId'> = {
							scheduleId: ':scheduleId',
							rotationId: ':rotationId',
						},
					): string => `/v1/schedules/${scheduleId}/rotations/${rotationId}`,
					order: {
						url: (
							{ scheduleId, rotationId }: PathParameters<'scheduleId' | 'rotationId'> = {
								scheduleId: ':scheduleId',
								rotationId: ':rotationId',
							},
						): string => `/v1/schedules/${scheduleId}/rotations/${rotationId}/order`,
					},
					name: {
						url: (
							{ scheduleId, rotationId }: PathParameters<'scheduleId' | 'rotationId'> = {
								scheduleId: ':scheduleId',
								rotationId: ':rotationId',
							},
						): string => `/v1/schedules/${scheduleId}/rotations/${rotationId}/name`,
					},
				},
			},
		},
		timeTemplates: {
			rotation: {
				url: (): '/v1/schedules/time-templates/rotation' => '/v1/schedules/time-templates/rotation',
			},
		},
	},
	customers: {
		timezones: {
			url: (): '/v1/customers/timezones' => '/v1/customers/timezones',
		},
	},
	uiPreference: {
		schedule: {
			team: {
				teamId: {
					url: ({ teamId }: PathParameters<'teamId'> = { teamId: ':teamId' }): string =>
						`/v1/users/ui-preference/schedule/team/${teamId}`,
				},
			},
		},
	},
} as const);

export { urls };
